/**
 * Per-device noise-meter calibration. The mic differs by machine (a laptop vs a
 * Promethean board), and "loud" depends on the room — so this is stored in
 * localStorage per device, not server-side per classroom.
 *
 * `sensitivity` scales the raw mic level before the zone thresholds apply:
 *   - higher → quieter sounds read louder (the meter trips to red sooner)
 *   - lower  → it takes more noise to count as "too loud"
 * So calibrating is "set what loud is": slide while the class makes the volume
 * you want flagged, until the meter turns red.
 */

export const MIN_SENSITIVITY = 0.4;
export const MAX_SENSITIVITY = 2.6;
export const DEFAULT_SENSITIVITY = 1;

const KEY = 'classy:noiseSensitivity';

export function clampSensitivity(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SENSITIVITY;
  return Math.min(MAX_SENSITIVITY, Math.max(MIN_SENSITIVITY, value));
}

export function getNoiseSensitivity(): number {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return DEFAULT_SENSITIVITY;
    return clampSensitivity(Number.parseFloat(raw));
  } catch {
    return DEFAULT_SENSITIVITY;
  }
}

export function setNoiseSensitivity(value: number): void {
  try {
    localStorage.setItem(KEY, String(clampSensitivity(value)));
  } catch {
    /* ignore quota / private mode */
  }
}
